import { DateTime } from "luxon";

import { CommonModels } from "@/data/common/common.models";
import type { ProfileModels } from "@/data/profile/profile.models";
import { logger } from "@/util/logger";
import { UrlUtils } from "@/util/url.utils";

export namespace ProfileUtils {
  export function emptyProfileData() {
    return {
      work_experience: [],
      volunteer_community: [],
      education: [],
      skills: [],
      achievements: {
        certifications: [],
        recognitions: [],
      },
      professional_bio: {
        summary: "",
        aspirations: [],
        key_strengths: [],
      },
    };
  }

  export function getDisplayAddress(location?: CommonModels.LocationDto | null): string {
    return [location?.city, location?.state].filter(Boolean).join(", ") || location?.msa_title || "";
  }

  export function getProfileSocials(userProfile?: ProfileModels.FullProfileResponseDto | null) {
    const socialMedia = userProfile?.dataV02?.person?.profile?.contact?.social_media;
    if (!socialMedia)
      return {
        linkedin: undefined,
        instagram: undefined,
        youtube: undefined,
        facebook: undefined,
      };

    const linkedin = UrlUtils.startLinkWithHttps(socialMedia.find((social) => social.name === "linkedin")?.url || "");
    const instagram = UrlUtils.startLinkWithHttps(socialMedia.find((social) => social.name === "instagram")?.url || "");
    const youtube = UrlUtils.startLinkWithHttps(socialMedia.find((social) => social.name === "youtube")?.url || "");
    const facebook = UrlUtils.startLinkWithHttps(socialMedia.find((social) => social.name === "facebook")?.url || "");
    return {
      linkedin,
      instagram,
      youtube,
      facebook,
    };
  }

  export function processSocialMediaData(socialMedia: {
    linkedin?: string | null;
    instagram?: string | null;
    youtube?: string | null;
    facebook?: string | null;
  }) {
    const processed = {
      linkedin: "",
      instagram: "",
      youtube: "",
      facebook: "",
    };

    // Process LinkedIn
    if (socialMedia.linkedin) {
      const handle = UrlUtils.parseLinkedInHandle(socialMedia.linkedin);
      if (handle) {
        processed.linkedin = UrlUtils.buildSocialMediaUrl(handle, "linkedin");
      } else if (!socialMedia.linkedin.includes("linkedin.com")) {
        // If it's just a handle, build the URL
        processed.linkedin = UrlUtils.buildSocialMediaUrl(socialMedia.linkedin, "linkedin");
      } else {
        processed.linkedin = UrlUtils.startLinkWithHttps(socialMedia.linkedin);
      }
    }

    // Process Instagram
    if (socialMedia.instagram) {
      const handle = UrlUtils.parseInstagramHandle(socialMedia.instagram);
      if (handle) {
        processed.instagram = UrlUtils.buildSocialMediaUrl(handle, "instagram");
      } else if (!socialMedia.instagram.includes("instagram.com")) {
        // If it's just a handle, build the URL
        processed.instagram = UrlUtils.buildSocialMediaUrl(socialMedia.instagram, "instagram");
      } else {
        processed.instagram = UrlUtils.startLinkWithHttps(socialMedia.instagram);
      }
    }

    // Process YouTube
    if (socialMedia.youtube) {
      const handle = UrlUtils.parseYouTubeHandle(socialMedia.youtube);
      if (handle) {
        processed.youtube = UrlUtils.buildSocialMediaUrl(handle, "youtube");
      } else if (!socialMedia.youtube.includes("youtube.com")) {
        // If it's just a handle, build the URL
        processed.youtube = UrlUtils.buildSocialMediaUrl(socialMedia.youtube, "youtube");
      } else {
        processed.youtube = UrlUtils.startLinkWithHttps(socialMedia.youtube);
      }
    }

    // Process Facebook
    if (socialMedia.facebook) {
      const handle = UrlUtils.parseFacebookHandle(socialMedia.facebook);
      if (handle) {
        processed.facebook = UrlUtils.buildSocialMediaUrl(handle, "facebook");
      } else if (!socialMedia.facebook.includes("facebook.com")) {
        // If it's just a handle, build the URL
        processed.facebook = UrlUtils.buildSocialMediaUrl(socialMedia.facebook, "facebook");
      } else {
        processed.facebook = UrlUtils.startLinkWithHttps(socialMedia.facebook);
      }
    }

    return processed;
  }

  export function extractSocialMediaHandles(socialMedia: {
    linkedin?: string | null;
    instagram?: string | null;
    youtube?: string | null;
    facebook?: string | null;
  }) {
    return {
      linkedin: UrlUtils.parseLinkedInHandle(socialMedia.linkedin || ""),
      instagram: UrlUtils.parseInstagramHandle(socialMedia.instagram || ""),
      youtube: UrlUtils.parseYouTubeHandle(socialMedia.youtube || ""),
      facebook: UrlUtils.parseFacebookHandle(socialMedia.facebook || ""),
    };
  }
  export function getProfilePollingEnabled(status?: CommonModels.ProfileStatusEnum) {
    if (!status) {
      return true;
    }
    return (
      status === CommonModels.ProfileStatusEnum.processing ||
      status === CommonModels.ProfileStatusEnum.queued ||
      status === CommonModels.ProfileStatusEnum.onboarding ||
      status === CommonModels.ProfileStatusEnum.initialized
    );
  }

  /**
   * Calculates total years of professional experience from work history
   * @param dataV02 - User profile data containing work history
   * @returns Total years of experience (rounded to nearest integer)
   */
  export function calculateYearsOfExperience(dataV02?: CommonModels.ProfileV2DataDto | null): number {
    if (!dataV02?.companies || dataV02.companies.length === 0) {
      return 0;
    }

    let totalMonths = 0;

    for (const company of dataV02.companies) {
      if (!company.positions || company.positions.length === 0) {
        continue;
      }

      for (const position of company.positions) {
        const startDate = position.period?.start_date;

        if (!startDate) {
          continue;
        }

        try {
          const start = DateTime.fromISO(startDate);

          if (!start.isValid) {
            logger.warn(`Invalid start date format: ${startDate}`);
            continue;
          }

          let end: DateTime;
          if (position.is_current) {
            end = DateTime.now();
          } else if (position.period?.end_date) {
            end = DateTime.fromISO(position.period.end_date);
            if (!end.isValid) {
              logger.warn(`Invalid end date format: ${position.period.end_date}`);
              continue;
            }
          } else {
            continue;
          }

          const diff = end.diff(start, "months");
          const months = Math.max(0, diff.months);
          totalMonths += months;
        } catch (error) {
          logger.error("Error calculating experience duration", error);
          continue;
        }
      }
    }

    return Math.round(totalMonths / 12);
  }
}
